/* ═══════════════════════════════════════════════════════════════
   Fields — key/value rows with inline pencil editing. Absorbs
   FieldRow/TextEditor (the old per-project Details widget) behaviour,
   fixing Josh's 2026-09-28 bug: `TextEditor` relied on
   `onBlur={cancel}` but every call site passed a no-op `cancel`, so
   single-line fields never closed on click-away. This file closes on
   a document-level mousedown-capture listener instead of `onBlur`, so
   there is no cancel-callback to wire wrong.

   Exit state machine is a pure reducer (`fieldsEditReducer`) so the
   three close paths are unit-testable without a DOM: outside-click and
   Esc both discard and close; Enter (or a select pick) closes and
   signals a 'save' effect. The component performs the actual async
   `onSave` outside the reducer; a rejection re-opens the field with an
   inline error instead of a toast, so the control stays in place.
   ═══════════════════════════════════════════════════════════════ */

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import { Icon } from '../ui/Icon';
import { Input } from '../ui/input';
import { SearchableSelect } from '../SearchableSelect';
import type { SelectOption } from '../SearchableSelect';
import { rem } from '../../lib/rem';

// ─── Pure exit state machine ───────────────────────────────────────

export interface FieldsEditState {
  editingKey: string | null;
}

export type FieldsEditAction =
  | { type: 'open'; key: string }
  | { type: 'outside' }
  | { type: 'esc' }
  | { type: 'enter' };

export type FieldsEditEffect = 'save' | 'focusPencil';

export interface FieldsEditResult {
  state: FieldsEditState;
  effects: FieldsEditEffect[];
}

/** Pure — no DOM, no async, no onSave call. `open→outside→closed` (no
 *  save effect, value untouched), `open→enter→closed` (+ 'save' effect),
 *  `open→esc→closed` (no save effect). */
export function fieldsEditReducer(
  state: FieldsEditState,
  action: FieldsEditAction
): FieldsEditResult {
  switch (action.type) {
    case 'open':
      return { state: { editingKey: action.key }, effects: [] };
    case 'outside':
    case 'esc':
      if (state.editingKey == null) return { state, effects: [] };
      return { state: { editingKey: null }, effects: ['focusPencil'] };
    case 'enter':
      if (state.editingKey == null) return { state, effects: [] };
      return { state: { editingKey: null }, effects: ['save', 'focusPencil'] };
    default:
      return { state, effects: [] };
  }
}

/** Outside-row predicate for the document-mousedown-capture close listener,
 *  extracted so it is unit-testable without a DOM. `target` is "inside" the
 *  row when it is a descendant of `rowEl` OR sits inside a body-portal
 *  element stamped `data-owner="<rowId>"` — a `SearchableSelect` menu lives
 *  on document.body (outside `rowEl`'s subtree) but is still logically part
 *  of this row's editor (Josh 2026-09-28 select-fields-never-save bug: the
 *  old `!rowRef.current.contains(e.target)` check unmounted the row, and
 *  therefore the portal, on every mousedown inside the portal). */
export function isOutsideRow(
  target: {
    closest?(selector: string): { getAttribute(name: string): string | null } | null;
  } | null,
  rowEl: { contains(node: unknown): boolean } | null,
  rowId: string
): boolean {
  if (!target) return true;
  if (rowEl && rowEl.contains(target)) return false;
  const owner = target.closest?.('[data-owner]');
  if (owner && owner.getAttribute('data-owner') === rowId) return false;
  return true;
}

// ─── Body ───────────────────────────────────────────────────────────

export type FieldEditorKind = 'text' | 'textarea' | 'select' | 'date' | 'number';

export interface FieldEditorConfig {
  kind: FieldEditorKind;
  options?: SelectOption[];
}

export interface FieldDef {
  key: string;
  label: string;
  value: string | null;
  display?: React.ReactNode;
  editor?: FieldEditorConfig;
  canEdit: boolean;
  onSave: (value: string) => Promise<void>;
  /** Forces this field's cell to span the full grid row (e.g. a long list
   *  of chips) even when its editor isn't a textarea, which spans by
   *  default. */
  span?: 'row';
}

export interface FieldsProps {
  fields: FieldDef[];
  /** 'compact' tightens row/label gaps and the minimum column width — the
   *  Project panel (2026-09-30, Josh: "details has so much spacing between
   *  each field") passes this; every other consumer is unaffected by the
   *  default. */
  density?: 'default' | 'compact';
}

function TextLikeEditor({ field, commit }: { field: FieldDef; commit: (v: string) => void }) {
  const inputType =
    field.editor?.kind === 'date' ? 'date' : field.editor?.kind === 'number' ? 'number' : 'text';
  return (
    <Input
      autoFocus
      type={inputType}
      defaultValue={field.value ?? ''}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit((e.target as HTMLInputElement).value);
      }}
      style={{ height: rem(30) }}
    />
  );
}

function TextareaEditor({ field, commit }: { field: FieldDef; commit: (v: string) => void }) {
  return (
    <textarea
      autoFocus
      defaultValue={field.value ?? ''}
      rows={3}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          commit((e.target as HTMLTextAreaElement).value);
        }
      }}
      className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
    />
  );
}

function SelectEditor({
  field,
  commit,
  ownerId,
}: {
  field: FieldDef;
  commit: (v: string) => void;
  ownerId: string;
}) {
  return (
    <SearchableSelect
      value={field.value ?? ''}
      options={field.editor?.options ?? []}
      onChange={(v) => commit(v)}
      ownerId={ownerId}
    />
  );
}

/** `instanceId` (one `useId()` per `Fields` instance) prefixes the owner id
 *  so two widgets on the same page that both have a `status` field (same
 *  `field.key`) don't collide on the same `data-owner` value — the
 *  outside-row check would otherwise treat a click in widget B's portal as
 *  "inside" widget A's row (Josh 2026-09-28 correction). */
function renderEditor(field: FieldDef, commit: (v: string) => void, instanceId: string) {
  const kind = field.editor?.kind ?? 'text';
  if (kind === 'textarea') return <TextareaEditor field={field} commit={commit} />;
  if (kind === 'select')
    return <SelectEditor field={field} commit={commit} ownerId={`${instanceId}:${field.key}`} />;
  return <TextLikeEditor field={field} commit={commit} />;
}

export function Fields({ fields, density = 'default' }: FieldsProps) {
  // One id per Fields instance — scopes `data-owner` (SearchableSelect
  // portal) and the outside-row rowId page-wide so two Fields cards with
  // the same field key (e.g. two 'status' fields) never collide.
  const instanceId = useId();
  const [editState, setEditState] = useState<FieldsEditState>({ editingKey: null });
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const pencilRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const rowRef = useRef<HTMLDivElement | null>(null);
  const editStateRef = useRef(editState);
  editStateRef.current = editState;

  const focusPencil = useCallback((key: string) => {
    requestAnimationFrame(() => pencilRefs.current[key]?.focus());
  }, []);

  const dispatch = useCallback(
    (action: FieldsEditAction) => {
      const prevKey = editStateRef.current.editingKey;
      const { state: next, effects } = fieldsEditReducer(editStateRef.current, action);
      setEditState(next);
      for (const effect of effects) {
        if (effect === 'save' && prevKey) {
          const field = fields.find((f) => f.key === prevKey);
          const value = pendingValueRef.current;
          if (field) {
            setErrors((e) => ({ ...e, [prevKey]: undefined }));
            field.onSave(value).catch((err: unknown) => {
              setErrors((e) => ({
                ...e,
                [prevKey]: err instanceof Error ? err.message : 'Save failed',
              }));
              setEditState({ editingKey: prevKey });
            });
          }
        }
        if (effect === 'focusPencil' && prevKey) focusPencil(prevKey);
      }
    },
    [fields, focusPencil]
  );

  const pendingValueRef = useRef('');
  const commit = useCallback(
    (value: string) => {
      pendingValueRef.current = value;
      dispatch({ type: 'enter' });
    },
    [dispatch]
  );

  useEffect(() => {
    const key = editState.editingKey;
    if (!key) return;
    const rowId = `${instanceId}:${key}`;
    function onDocMouseDown(e: MouseEvent) {
      if (isOutsideRow(e.target as Element | null, rowRef.current, rowId)) {
        dispatch({ type: 'outside' });
      }
    }
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [editState.editingKey, dispatch, instanceId]);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fill, minmax(${density === 'compact' ? '16rem' : '18rem'}, 1fr))`,
        gap: density === 'compact' ? 'var(--space-2)' : 'var(--space-3)',
      }}
    >
      {fields.map((field) => {
        const editing = editState.editingKey === field.key;
        const error = errors[field.key];
        const spansRow = field.span === 'row' || field.editor?.kind === 'textarea';
        return (
          <div
            key={field.key}
            ref={editing ? rowRef : undefined}
            className="flex flex-col"
            style={{
              gap: 'var(--space-1)',
              ...(spansRow ? { gridColumn: '1 / -1' } : undefined),
            }}
          >
            <div className="flex items-center gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted">
                {field.label}
              </span>
              {field.canEdit && !editing && (
                <button
                  ref={(el) => {
                    pencilRefs.current[field.key] = el;
                  }}
                  type="button"
                  aria-label={`Edit ${field.label}`}
                  title={`Edit ${field.label}`}
                  onClick={() => dispatch({ type: 'open', key: field.key })}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-md text-muted transition-colors hover:bg-hover hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <Icon icon={Pencil} size="sm" />
                </button>
              )}
            </div>
            {editing ? (
              <div
                onKeyDown={(e) => {
                  if (e.key === 'Escape') dispatch({ type: 'esc' });
                }}
              >
                {renderEditor(field, commit, instanceId)}
                {error && <div className="text-xs text-danger">{error}</div>}
              </div>
            ) : (
              <div className="text-base text-text" style={{ minHeight: rem(22) }}>
                {field.display ?? field.value ?? '—'}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
